import React from 'react';
import { Link } from 'react-router-dom';

import * as Yup from 'yup';

import Modal from 'antd/es/modal';

import { FieldProps } from 'formik';

import FormikField from '@common/react/components/Forms/FormikField/FormikField';
import { ConfirmationCodeInput } from '@common/react/components/Forms/ConfirmationCodeInput/ConfirmationCodeInput';
import { PreventDefaultButton } from '@common/react/components/Forms/Button';
import { simpleStringValidator } from '@common/react/utils/validationHelpers';
import { ItemProvider } from '@common/react/components/Core/ItemProvider/ItemProvider';
import { ItemEditor } from '@common/react/components/Core/ItemEditor/ItemEditor';
import { WithId } from '@common/typescript/objects/WithId';
import { RequestButton } from '@common/react/components/UI/LoadingButton/LoadingButton';
import { BaseUser } from '@common/typescript/objects/BaseUser';
import { useApplicationContext } from '@common/react/components/Core/Application/Application';

interface Props {
	phoneNumber: string;
	setPasswordByPhoneRequest?: string;
	resendSetPasswordByPhoneCodeRequest?: string;
	topMessage?: string;
	withFieldTitle?: boolean;
	onBack?: () => void;
	afterLoginPath?: string;
	recoverPath?: string;
}

interface ConfirmCode extends WithId {
	path: string;
	password: string;
	repeat: string;
	code: string;
	phoneNumber: string;
}

const validationSchema = Yup.object().shape({
	code: simpleStringValidator,
	password: simpleStringValidator,
	repeat: Yup.string().oneOf([Yup.ref('password'), null], 'Passwords are not equal').required(),
});

const SetPassword: <User extends BaseUser>(p: Props) => React.ReactElement<User> = <User extends BaseUser, >(props) => {
	const {
		phoneNumber,
		setPasswordByPhoneRequest = 'setPasswordAndLoginByPhoneNumber',
		resendSetPasswordByPhoneCodeRequest = 'resendRecoverByPhoneCode',
		topMessage = 'A phone number verification message has been sent to your current phone number.',
		withFieldTitle,
		onBack,
		afterLoginPath = '/dashboard',
		recoverPath = '/recover',
	} = props;
	const [time, setTime] = React.useState(30);
	const ref = React.useRef<any>(null);

	const [success, setSuccess] = React.useState<boolean>(false);

	const { handleLogin } = useApplicationContext();

	const startTimer = () => {
		ref.current = null;
		setTime(60);
		ref.current = setInterval(() => {
			setTime((prev) => {
				const newTime = prev - 1;
				if (!newTime && ref.current) {
					clearInterval(ref.current);
					ref.current = null;
				}
				return newTime;
			});
		}, 1000);
		return () => {
			if (ref.current) {
				clearInterval(ref.current);
			}
		};
	};

	React.useEffect(() => {
		startTimer();
	}, []);

	if (success) {
		return (
			<div className="text-center">
				<h4 style={{ marginTop: 0, fontSize: 16, lineHeight: 1.4 }}>
					<div>
						<i className="fa fa-check-circle-o" style={{ fontSize: 90, marginBottom: 10 }} />
					</div>
					Your password has been successfully changed
				</h4>
				<br />
				<Link to={afterLoginPath} className="btn btn-primary">Login</Link>
			</div>
		);
	}

	return (
		<>
			<div className="clearfix">
				<div className="site-headline text-center">
					<h1>Setup password</h1>
				</div>
				<p className="text-center">
					{topMessage}
				</p>
			</div>
			<ItemProvider<ConfirmCode>
				id={-1}
				saveRequest={setPasswordByPhoneRequest}
				add={{
					path: '/',
					code: '',
					password: '',
					repeat: '',
					phoneNumber,
				}}
				validationSchema={validationSchema}
				resetAfterSave
			>
				<ItemEditor<ConfirmCode, { initObject }>
					successMessage=""
					afterSubmit={({ res }) => {
						handleLogin(res?.initObject);

						setSuccess(true);
						// history.push('/login');
					}}
					edit={({ formikBag, state: { loading } }) => <>
						<div className="row">
							<FormikField
								fieldName="password"
								inputProps={{ placeholder: withFieldTitle ? '' : 'New Password*', type: 'password' }}
								containerClassName="col-sm-12 form-group"
								title={withFieldTitle ? 'New Password*' : ''}
							/>
							<FormikField
								fieldName="repeat"
								inputProps={{ placeholder: 'Repeat New Password*', type: 'password' }}
								containerClassName="col-sm-12 form-group"
								title={withFieldTitle ? 'Repeat New Password*' : ''}
							/>
							<br />
							<FormikField
								containerClassName="col-sm-12 form-group mt10"
								fieldName="code"
								title=""
								render={(fieldProps: FieldProps) =>
									<div className="text-center">
										<label htmlFor="code">Code</label>
										<ConfirmationCodeInput
											fieldProps={fieldProps}
										/>
									</div>
								}
							/>
						</div>
						<div className="clearfix" style={{ marginTop: 15, marginBottom: 15 }}>
							<PreventDefaultButton
								type="submit"
								className="btn btn-primary col-sm-6 col-sm-offset-3"
								isLoading={loading}
								onClick={formikBag.handleSubmit}
							>
								Confirm
							</PreventDefaultButton>
						</div>
					</>}
				/>
				<div className="text-center">
					Didn't receive a code?
					{' '}
					{time ? `Resend code in ${time} seconds.`
						: (
							<RequestButton
								className="recover-page__resend"
								beforeAction={() => {
									startTimer();
									return true;
								}}
								onError={(e) => {
									Modal.error({
										title: 'Error',
										content: e,
									});

									if (ref.current) {
										clearInterval(ref.current);
										ref.current = null;
										setTime(0);
										startTimer();
									}
								}}
								requestType={resendSetPasswordByPhoneCodeRequest}
								requestProps={{ phoneNumber }}
								successMessage="The confirmation code has been successfully sent."
							>
								Resend code
							</RequestButton>)}
					<div className="mb10" />
					{onBack ? <a onClick={onBack}>Try another method</a> : <Link to={recoverPath}>
						Try another method
					</Link>}
				</div>
			</ItemProvider>
		</>
	);
};

export default SetPassword;
